import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { SiteDoc, SiteShot } from '../../../shared/siteContent';
import { PREVIEW_DRAFT, PREVIEW_HEIGHT, PREVIEW_READY } from '../../site/useSiteContent';

/**
 * The draft, drawn by the real site (the handoff: "render the real HomePage /
 * FeaturesPage … don't maintain a second hand-built copy").
 *
 * The public page is framed at `?preview=draft` and posted the working copy
 * whenever it changes; it draws that instead of what is published, as a
 * stranger sees it, and posts back its height. On a desktop the frame is a
 * 1280px-wide page scaled to the card, so the preview is the desktop layout
 * in miniature; `native` draws it at the box's own width instead — the
 * phone sheet, where a page scaled from 1280 would be unreadable and the
 * phone layout is the one worth seeing. The frame takes no pointer and no
 * focus: it is a picture, and a link followed inside it would lose the draft.
 */
export default function SitePreview({ path, doc, shots, native }: {
  /** "/" or "/features" */
  path: string;
  doc: SiteDoc;
  shots: SiteShot[];
  native?: boolean;
}) {
  const box = useRef<HTMLDivElement>(null);
  const frame = useRef<HTMLIFrameElement>(null);
  const ready = useRef(false);
  const [boxWidth, setBoxWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const pageWidth = native ? boxWidth : 1280;
  const scale = native || !boxWidth ? 1 : boxWidth / 1280;

  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return undefined;
    const measure = () => setBoxWidth(el.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const latest = useRef({ doc, shots });
  latest.current = { doc, shots };
  const post = () => frame.current?.contentWindow?.postMessage(
    { type: PREVIEW_DRAFT, doc: latest.current.doc, shots: latest.current.shots }, window.location.origin,
  );

  useEffect(() => {
    const onMessage = (e: MessageEvent) => {
      if (e.origin !== window.location.origin || e.source !== frame.current?.contentWindow) return;
      const d = e.data as { type?: string; height?: number } | null;
      if (d?.type === PREVIEW_READY) { ready.current = true; post(); }
      if (d?.type === PREVIEW_HEIGHT && typeof d.height === 'number') setHeight(d.height);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  // Each edit reaches the frame as it is made; a new page (Home ↔ Features)
  // is a new frame, which says it is ready again.
  useEffect(() => { if (ready.current) post(); }, [doc, shots]);
  useEffect(() => { ready.current = false; setHeight(0); }, [path]);

  const frameHeight = height || 1600;
  return (
    <div className={`sc-preview-body${native ? ' native' : ''}`} ref={box}>
      <div className="sc-preview-extent" style={{ height: frameHeight * scale }}>
        <iframe
          ref={frame}
          key={path}
          className="sc-preview-frame"
          title={`Preview of ${path === '/' ? 'the home page' : 'the features page'} as a visitor sees it`}
          src={`${path}?preview=draft`}
          tabIndex={-1}
          style={{ width: pageWidth || '100%', height: frameHeight, transform: scale === 1 ? undefined : `scale(${scale})` }}
        />
      </div>
    </div>
  );
}
